import {useEffect, useState} from 'react';
import {
  Tabs as CommonTabs,
  TabPane as CommonTabPane,
} from '@/components/common-components/components';
import {
  Spin,
} from 'antd';
import {
  LoadingOutlined,
} from '@ant-design/icons';
import styles from './style.module.scss';
import {getLocalesByKey as t} from '@/utils/translation';
// import {VideoPopOver} from '@/components/common-components/components/popover';
import {FocusTermsTab} from './FocusTermsTab/FocusTermsTab';
import {observer} from 'mobx-react';
import {LinkSuggestionsTab} from './LinkSuggestionsTab/LinkSuggestionsTab';
import {useStore} from '@/store/root-store';
import styled from 'styled-components';
import {QuestionsTab} from './QuestionsTab/QuestionsTab';
import {MixPanel} from '@/utils/mixpanel';
import {SCA_PAGE_FOCUS_TERMS_TAB_OPEN, SCA_PAGE_QUESTIONS_TAB_OPEN, SCA_PAGE_LINK_SUGGESTIONS_TAB_OPEN} from '@/constants/events';
import {SidebarTooltip} from '../../Common/SideBarTooltip/sidebarTooltip';
import {isScaPublicPage} from '@/utils/url';
import AddFocusTermModal from './DropdownController/AddFocusTermModal';

interface FocusTermsTabsProps {
  viewSuggestionsOption: boolean;
  linksDropDownFromSuggestion: 'links' | 'terms';
}
const secondAntIcon = <LoadingOutlined className={styles.customSpinner} style={{marginTop: '-3px', fontSize: '12px important', color: '#7f4ead', marginLeft: '5px'}} spin />;

export const FocusTermsTabs = observer(({viewSuggestionsOption, linksDropDownFromSuggestion}: FocusTermsTabsProps) => {
  const {
    contentOptimizer: {
      currentPage,
    }} = useStore('');

  const [activeKey, setActiveKey] = useState('1');

  useEffect(() => {
    viewSuggestionsOption ? onChangeTab('3') : '';
  }, [viewSuggestionsOption]);

  const onChangeTab = key => {
    setActiveKey(key);
    switch (key) {
      case '1':
        MixPanel.track(SCA_PAGE_FOCUS_TERMS_TAB_OPEN, {
          'pageId': currentPage.content.uuid,
          'description': 'Focus Terms tab in right sidebar was opened.',
        });
        break;
      case '2':
        MixPanel.track(SCA_PAGE_QUESTIONS_TAB_OPEN, {
          'pageId': currentPage.content.uuid,
          'description': 'Questions tab in right sidebar was opened.',
        });
        break;
      case '3':
        MixPanel.track(SCA_PAGE_LINK_SUGGESTIONS_TAB_OPEN, {
          'pageId': currentPage.content.uuid,
          'description': 'Link suggestions tab in right sidebar was opened.',
        });
        break;
    }
  };

  const isNotPublic = !isScaPublicPage();

  return (
    <div>
      <AddFocusTermModal showAddFocusTermModal={currentPage.showAddFocusTermModal} setShowAddFocusTermModal={currentPage.setShowAddFocusTermModal} />
      <CommonTabsStyled
        // style={currentPage.focusTermsExpanded ? {marginTop: '50px'} : {}}
        tabBarGutter={30}
        activeKey={activeKey}
        tabBarStyle={{'padding': '0 !important'}}
        defaultActiveKey={'1'}
        onChange={key => onChangeTab(key)}
      >
        <TabsStyled
          expanded={currentPage.focusTermsExpanded}
          // this prop is very important, it will expand this section enough to fill the bottom of the screen
          // when the editor is scrolled enough for top bar to dissapear
          editorScrolled={currentPage.isEditorScrolled}
          tab={
            <SidebarTooltip title='Words semantically related to a primary or target keyword. Including Topical Terms (Focus Terms) within your content can increase your Content Score, which reflects its potential to rank'>
              <TabTitleContainer active={activeKey === '1'}>
                <div className={'tabTitle'} style={{fontSize: currentPage.focusTermsExpanded ? '12px' : '14px'}}>
                  {t('topical-terms')}
                </div>
                <div className={'tabExtraInfo'}>
                  {
                    currentPage?.isAnalyzing ?
                      (<Spin indicator={secondAntIcon}/>) :
                      (<div className={'tabUsageContainer'}>
                        <div className={'usageValue'}>{currentPage?.analytics?.totalUsedTerms}</div>
                        <div className={'totalValue'}><span style={{marginLeft: 3, marginRight: 3}}>/</span><span>{currentPage?.analytics?.focusTerms?.length}</span></div>
                      </div>)
                  }
                </div>
              </TabTitleContainer>
            </SidebarTooltip>
          }
          key='1'
          style={{'outline': 'none'}}>
          <FocusTermsTab />
        </TabsStyled>

        <TabsStyled
          expanded={currentPage.focusTermsExpanded}
          // this prop is very important, it will expand this section enough to fill the bottom of the screen
          // when the editor is scrolled enough for top bar to dissapear
          editorScrolled={currentPage.isEditorScrolled}
          tab={
            <SidebarTooltip title='Questions related to the selected target keywords.'>
              <TabTitleContainer active={activeKey === '2'}>
                <div className={'tabTitle'} style={{fontSize: currentPage.focusTermsExpanded ? '12px' : '14px'}}>
                Questions
                </div>
                <div className={'tabExtraInfo'}>
                  {
                    currentPage?.isAnalyzing ?
                      (<Spin indicator={secondAntIcon}/>) :
                      (<div className={'tabUsageContainer'}>
                        <div className={'usageValue'}>{currentPage?.analytics?.totalUsedQuestions}</div>
                        <div className={'totalValue'}><span style={{marginLeft: 3, marginRight: 3}}>/</span><span>{currentPage?.analytics?.questions?.length}</span></div>
                      </div>)
                  }
                </div>
              </TabTitleContainer>
            </SidebarTooltip>
          }
          key='2'
          style={{'outline': 'none'}}>
          {isNotPublic && <QuestionsTab />}
        </TabsStyled>

        <TabsStyled
          expanded={currentPage.focusTermsExpanded}
          // this prop is very important, it will expand this section enough to fill the bottom of the screen
          // when the editor is scrolled enough for top bar to dissapear
          editorScrolled={currentPage.isEditorScrolled}
          tab={
            <SidebarTooltip title='Internal and External Link suggestions to increase your rankings.'>
              <TabTitleContainer active={activeKey === '3'}>
                <div className={'tabTitle'} style={{fontSize: currentPage.focusTermsExpanded ? '12px' : '14px'}}>
                  Link Suggestions
                </div>
                <div className={'tabExtraInfo'}>
                  {currentPage?.isFetchingIntLinksSug ?
                    (<Spin indicator={secondAntIcon}/>) :
                    (<div className={'tabUsageContainer'}>
                      <div className={'usageValue'}>{currentPage?.content?.totalUsedSuggestions}</div>
                      <div className={'totalValue'}><span style={{marginLeft: 3, marginRight: 3}}>/</span><span>{currentPage?.content?.internalLinkSuggestionsV2?.allLinks?.length}</span></div>
                    </div>)
                  }
                </div>
              </TabTitleContainer>
            </SidebarTooltip>
          }
          key='3'
          style={{'outline': 'none'}}>
          <LinkSuggestionsTab linksDropDownFromSuggestion={linksDropDownFromSuggestion} viewSuggestionsOption={viewSuggestionsOption}/>
        </TabsStyled>
      </CommonTabsStyled>
    </div>
  );
});

const CommonTabsStyled = styled(CommonTabs)`
  .rc-tabs-extra-content {
    background-color: transparent !important;
    border: 1px solid $color-gray-dark-medium;
    border-radius: 5px;
    cursor: pointer;
    font-size: $font-size-12;
    height: 25px;
    margin-bottom: auto;
    margin-left: 5px;
    margin-top: auto;
    padding: 0 5px;
    transition: all 0.2s ease-in;
  }

  .focusTermsTabTitleValue {
    color: $color-gray-light;
    display: block;
    font-size: $font-size-12;
    font-weight: 400;
    margin-left: 5px;

    .ant-spin-spinning {
      height: 10px;
    }

    .customSpinner {
      font-size: 12px !important;
    }
  }

  .rc-tabs-content-holder {
    background-color: transparent !important;
    padding-top: 15px;
  }

  .rc-tabs-nav {
    border-bottom: 1px solid #4E5156;
  }
  .rc-tabs-nav-list {
    justify-content: space-between;
  }
  .rc-tabs-nav-operations {
    display: none !important;
  }

  .rc-tabs-tab {
    margin-left: 20px !important;

    &:first-child {
      margin-left: 0px !important;
    }
  }

  @media screen and (max-width: 1400px) {
    .rc-tabs-tab {
      margin-left: 0px !important;
      margin-right: 15px !important;
    }
 
  }

  .rc-tabs-tab-btn {
    padding: 5px 0 !important;
  }

  .rc-tabs-ink-bar {
    background-color: #F1AA3E !important;
    border-radius: 2px;
  }
`;

const TabsStyled = styled(CommonTabPane)<{expanded: boolean; editorScrolled: boolean}>`
  
  .ant-collapse {
    overflow-y: ${p => p.expanded ? 'auto' : 'inherit'};
    height:  ${p => p.expanded ? `calc(100vh - ${p.editorScrolled ? 292 : 300}px)` : 'auto'};
    // For Internet Explorer
    scrollbar-face-color: rgba(138,138,138,0.32);
    scrollbar-track-color: rgba(138,138,138,0.12);

    // For Google Chrome
    &::-webkit-scrollbar {
      height: 5px;
      width: 5px;
    }
  
    &::-webkit-scrollbar-thumb {
      background: rgba(138,138,138,0.32);
    }
  
    &::-webkit-scrollbar-track {
      background: rgba(138,138,138,0.12);
    }
  }
`;

const TabTitleContainer = styled.div<{active: boolean}>`

  .tabTitle {
    font-weight: 400;
    color: ${p => p.active ? '#fff' : '#A3A4A4'}
  }


  .tabExtraInfo {
    padding-bottom: 8px;

    .tabUsageContainer {
      display: flex;

      .usageValue {
        font-size: 18px;
        font-weight: 600;
        color: ${p => p.active ? '#fff' : '#A3A4A4'}
      }

      .totalValue {
        span {
          font-size: 12px;
          color: #A3A4A4;
        }
      }
    }
  }

`;
